import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Grid,
  Input,
  Pagination,
  Rate,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
  SortAscendingOutlined,
  StarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import PageShell from '../components/Layout/PageShell';
import { vendorAPI } from '../services/api';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchStores } from '../store/slices/storesSlice';
import type { ApiResponse, Review, VendorReviewsSummary } from '../types';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

type SortKey = 'newest' | 'oldest' | 'rating_high' | 'rating_low';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'rating_high', label: 'Highest rating' },
  { value: 'rating_low', label: 'Lowest rating' },
];

function reviewCustomerName(review: Review): string {
  const u = review.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const p = (u as { profile?: { firstName?: string; lastName?: string } }).profile;
    const n = [p?.firstName, p?.lastName].filter(Boolean).join(' ');
    if (n) return n;
  }
  return 'Customer';
}

function reviewCustomerAvatar(review: Review): string | undefined {
  const u = review.user;
  if (u && typeof u === 'object' && 'avatar' in u) {
    const a = (u as { avatar?: string }).avatar;
    if (a) return a;
  }
  return undefined;
}

function storeLabel(review: Review): string {
  const s = review.store;
  if (s && typeof s === 'object' && 'storeName' in s) {
    return (s as { storeName?: string }).storeName || 'Store';
  }
  return 'Store';
}

function listingLabel(review: Review): string {
  const l = review.listing;
  if (l && typeof l === 'object' && 'title' in l) {
    return (l as { title?: string }).title || 'Listing';
  }
  return 'Store review';
}

function listingThumb(review: Review): string | undefined {
  const l = review.listing;
  if (!l || typeof l !== 'object' || !('images' in l)) return undefined;
  const imgs = (l as { images?: Array<string | { url?: string }> }).images;
  if (!imgs?.length) return undefined;
  const first = imgs[0];
  if (typeof first === 'string') return first;
  return first?.url;
}

function statusTag(status: string | undefined) {
  if (!status) return null;
  const color =
    status === 'approved' ? 'green' : status === 'pending' ? 'orange' : status === 'rejected' ? 'red' : 'default';
  return <Tag color={color}>{status.toUpperCase()}</Tag>;
}

const Reviews: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stores } = useAppSelector((s) => s.stores);
  const screens = Grid.useBreakpoint();
  const useCompactFilters = screens.md === false;

  const [reviews, setReviews] = useState<Review[]>([]);
  const [summary, setSummary] = useState<VendorReviewsSummary>({ total: 0, averageRating: 0, positiveCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [storeFilter, setStoreFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'pending' | 'approved' | 'rejected'>('');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchStores({ page: 1, limit: 200 }));
  }, [dispatch]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const prevSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevSearchRef.current === null) {
      prevSearchRef.current = debouncedSearch;
      return;
    }
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setPage(1);
    }
  }, [debouncedSearch]);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page,
        limit: pageSize,
        sort: sortKey,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (storeFilter) params.store = storeFilter;
      if (ratingFilter !== '') params.rating = ratingFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await vendorAPI.getReviews(params);
      const body = res.data as ApiResponse<{ reviews: Review[]; summary: VendorReviewsSummary }>;
      const data = body.data;
      const pagination = body.pagination;
      if (data?.reviews) setReviews(data.reviews);
      if (data?.summary) setSummary(data.summary);
      if (pagination) {
        setPaginationMeta({
          totalPages: pagination.totalPages,
          totalItems: pagination.totalItems,
          itemsPerPage: pagination.itemsPerPage,
        });
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Could not load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortKey, debouncedSearch, storeFilter, ratingFilter, statusFilter]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const storeOptions = useMemo(
    () => [{ value: '', label: 'All stores' }, ...stores.map((s) => ({ value: s._id, label: s.storeName }))],
    [stores]
  );

  const filterToolbar = (
    <Space direction="vertical" size="middle" className="w-full">
      <Input
        allowClear
        prefix={<SearchOutlined className="text-slate-400" />}
        placeholder="Search comments or titles…"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className="!rounded-xl"
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          className="w-full"
          value={storeFilter || undefined}
          options={storeOptions}
          onChange={(v) => {
            setStoreFilter(v ?? '');
            setPage(1);
          }}
          placeholder="Store"
          suffixIcon={<ShopOutlined />}
        />
        <Select
          className="w-full"
          value={ratingFilter === '' ? undefined : ratingFilter}
          allowClear
          placeholder="Any rating"
          options={[
            { value: 5, label: '5 stars' },
            { value: 4, label: '4 stars' },
            { value: 3, label: '3 stars' },
            { value: 2, label: '2 stars' },
            { value: 1, label: '1 star' },
          ]}
          onChange={(v) => {
            setRatingFilter(v ?? '');
            setPage(1);
          }}
        />
        <Select
          className="w-full"
          value={statusFilter || undefined}
          allowClear
          placeholder="Moderation status"
          options={[
            { value: 'approved', label: 'Approved' },
            { value: 'pending', label: 'Pending' },
            { value: 'rejected', label: 'Rejected' },
          ]}
          onChange={(v) => {
            setStatusFilter((v as '' | 'pending' | 'approved' | 'rejected') || '');
            setPage(1);
          }}
        />
        <Select
          className="w-full"
          value={sortKey}
          options={SORT_OPTIONS}
          onChange={(v) => {
            setSortKey(v as SortKey);
            setPage(1);
          }}
          suffixIcon={<SortAscendingOutlined />}
        />
      </div>
    </Space>
  );

  const avgDisplay = summary.total > 0 ? summary.averageRating.toFixed(1) : '—';

  return (
    <PageShell
      title="Customer reviews"
      description="Read feedback from buyers across your stores. Filter by shop, rating, or moderation status."
      className="dashboard-surface"
      titleEnd={
        <Button icon={<ReloadOutlined />} onClick={() => void loadReviews()} loading={loading} className="rounded-xl">
          Refresh
        </Button>
      }
    >
      {error ? (
        <Alert type="error" showIcon message={error} className="mb-4 rounded-2xl" />
      ) : null}

      <div className="space-y-5">
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={8}>
            <Card className="rounded-2xl border-slate-200 shadow-sm" styles={{ body: { padding: '1.25rem' } }}>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Average rating</p>
              {loading && !reviews.length ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-900">{avgDisplay}</p>
                  <Rate disabled allowHalf value={summary.averageRating} className="mt-1 text-sm" />
                </>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="rounded-2xl border-slate-200 shadow-sm" styles={{ body: { padding: '1.25rem' } }}>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Matching reviews</p>
              {loading && !reviews.length ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <p className="mt-1 text-3xl font-semibold tabular-nums text-[#0171BF]">{summary.total}</p>
              )}
              <p className="mt-1 text-sm text-slate-600">With your current filters</p>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="rounded-2xl border-slate-200 shadow-sm" styles={{ body: { padding: '1.25rem' } }}>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">4–5 star reviews</p>
              {loading && !reviews.length ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <p className="mt-1 text-3xl font-semibold tabular-nums text-emerald-600">{summary.positiveCount}</p>
              )}
              <p className="mt-1 text-sm text-slate-600">Positive in this filter set</p>
            </Card>
          </Col>
        </Row>

        <Card
          className="rounded-2xl border-slate-200 shadow-sm"
          title={<span className="font-semibold text-slate-900">All reviews</span>}
          extra={
            useCompactFilters ? (
              <Button icon={<FilterOutlined />} onClick={() => setFiltersDrawerOpen(true)}>
                Filters
              </Button>
            ) : null
          }
        >
          {!useCompactFilters ? filterToolbar : null}

          <div className={useCompactFilters ? '' : 'mt-4'}>
            {loading && !reviews.length ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : reviews.length === 0 ? (
              <Empty
                description="No reviews match your filters yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="py-10"
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {reviews.map((review) => (
                  <div key={review._id} className="flex flex-col gap-3 py-4 first:pt-0 sm:flex-row sm:gap-4">
                    <Avatar size={48} src={reviewCustomerAvatar(review)} icon={<StarOutlined />} className="shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Text strong className="text-slate-900">
                          {reviewCustomerName(review)}
                        </Text>
                        <Rate disabled value={review.rating} className="text-sm" />
                        {statusTag(review.status)}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Tag className="m-0 border-slate-200 bg-slate-50 text-slate-700">{storeLabel(review)}</Tag>
                        <Tag color="processing" className="m-0">
                          {listingLabel(review)}
                        </Tag>
                      </div>
                      {review.title ? (
                        <Title level={5} className="!mb-1 !mt-3 !text-base !font-semibold text-slate-900">
                          {review.title}
                        </Title>
                      ) : null}
                      <p className="text-sm leading-relaxed text-slate-700">{review.comment}</p>
                      {review.vendorResponse?.message ? (
                        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                          <Text type="secondary" className="text-xs font-medium uppercase">
                            Your reply
                          </Text>
                          <p className="mb-0 mt-1 text-sm text-slate-700">{review.vendorResponse.message}</p>
                          {review.vendorResponse.respondedAt ? (
                            <Text type="secondary" className="text-xs">
                              {dayjs(review.vendorResponse.respondedAt).format('MMM D, YYYY h:mm A')}
                            </Text>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span title={dayjs(review.createdAt).format()}>{dayjs(review.createdAt).fromNow()}</span>
                        {(review.helpful?.count ?? 0) > 0 ? (
                          <span>{review.helpful?.count} found this helpful</span>
                        ) : null}
                        {listingThumb(review) ? (
                          <img
                            src={listingThumb(review)}
                            alt=""
                            className="ml-auto hidden h-12 w-12 rounded-lg object-cover sm:block"
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {reviews.length > 0 && paginationMeta.totalPages > 1 ? (
            <div className="mt-6 flex justify-center border-t border-slate-100 pt-4">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={paginationMeta.totalItems}
                showSizeChanger
                pageSizeOptions={['10', '20', '50']}
                onChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
              />
            </div>
          ) : null}
        </Card>
      </div>

      <Drawer
        title="Filters"
        placement="bottom"
        height="min(85vh, 520px)"
        onClose={() => setFiltersDrawerOpen(false)}
        open={filtersDrawerOpen}
      >
        {filterToolbar}
        <Button type="primary" block className="mt-6 rounded-xl bg-[#0171BF]" onClick={() => setFiltersDrawerOpen(false)}>
          Done
        </Button>
      </Drawer>
    </PageShell>
  );
};

export default Reviews;
